import type { FileUploadRestrictions } from '../types';
import type { TFunction } from 'i18next';

import {
  getFormattedNumber,
  isPlainObject,
  currentIntlLocale,
  formatFileSize,
} from '@vestigas/utils';
import { formatFileType } from '~/utils';

export type GetRestrictionsHintOptions = {
  locale?: string;
  t: TFunction<'ui'>;
};

/**
 * Helper function to return a human readable hint for an Uppy restrictions object.
 *
 * Examples:
 * - "You can upload 1 PDF file"
 * - "You can upload up to 3 files"
 * - "You can upload up to 3 PDF, JPG, or PNG files with up to 10 MB each"
 *
 * @param restrictions - The Uppy restrictions object
 * @param options - Translation function and optional locale
 * @returns A human readable hint
 */
export function getRestrictionsHint(
  restrictions: FileUploadRestrictions | undefined,
  options: GetRestrictionsHintOptions,
) {
  if (!restrictions || !isPlainObject(restrictions)) {
    return '';
  }

  const hasRestrictions =
    Object.values(restrictions).filter((value) => {
      return (
        (Array.isArray(value) && value.filter(Boolean).length > 0) ||
        (typeof value === 'number' && value > 0)
      );
    }).length > 0;

  if (!hasRestrictions) {
    return '';
  }

  const { locale = currentIntlLocale(), t } = options;
  const { allowedFileTypes = [], maxFileSize, maxNumberOfFiles } = restrictions;

  const hasFileTypes = allowedFileTypes.length > 0;
  const maxFiles = maxNumberOfFiles ?? 1;
  const fileCount = maxFiles;

  const fileTypesLabel = hasFileTypes
    ? new Intl.ListFormat(locale, {
        type: 'disjunction',
        style: 'long',
      }).format(allowedFileTypes.map((type) => formatFileType(type, t)))
    : '';

  const maxNumberLabel =
    fileCount === 1
      ? getFormattedNumber({ value: 1, digits: 0, locale })
      : t(($) => $.uploader.restrictionsHint.upTo, {
          count: fileCount,
        });

  const fileLabel =
    fileCount === 1
      ? t(($) => $.uploader.restrictionsHint.file)
      : t(($) => $.uploader.restrictionsHint.files);

  const sizeHint =
    maxFileSize != null
      ? t(($) => $.uploader.restrictionsHint.sizeHint, {
          size: formatFileSize(maxFileSize, locale),
        })
      : '';

  if (hasFileTypes) {
    return t(($) => $.uploader.restrictionsHint.withFileTypes, {
      fileLabel,
      fileTypes: fileTypesLabel,
      maxNumber: maxNumberLabel,
      sizeHint,
    });
  }

  return t(($) => $.uploader.restrictionsHint.withoutFileTypes, {
    fileLabel,
    maxNumber: maxNumberLabel,
    sizeHint,
  });
}
